"use strict";
// Copyright © Veeam Software Group GmbH
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DataGrid = void 0;
const react_1 = __importStar(require("react"));
const core_1 = require("@veeam/core");
const DataGridInteractor_1 = require("./DataGridInteractor");
const hooks_1 = require("./hooks");
const IgnoreSvg_1 = require("./icons/IgnoreSvg");
const RefreshSvg_1 = require("./icons/RefreshSvg");
const Grid_1 = require("../Grid");
const helpers_1 = require("../helpers");
const icons_1 = require("../icons");
const layouts_1 = require("../layouts");
const collectionContextMenu_1 = require("../smart/collectionContextMenu");
const collectionToolbar_1 = require("../smart/collectionToolbar");
const Text_1 = require("../Text");
function DataGrid({ model, disabled, loading, toolbar, columns, config, flags = {}, texts, fragment, isRowDisabled, isRowInactive, disabledSelection, apiController, onRowClick, onRowDoubleClick }) {
    var _a, _b;
    const { showNotification, dismissNotification, notificationContent, canCloseNotification } = (0, hooks_1.useDataGridNotification)();
    const gridApi = {
        showNotification,
        dismissNotification
    };
    const { interactor } = (0, core_1.useInteractor)(() => {
        var _a;
        return new DataGridInteractor_1.DataGridInteractor(model, {
            autoLoad: (_a = config === null || config === void 0 ? void 0 : config.autoLoad) !== null && _a !== void 0 ? _a : true,
            persistent: config === null || config === void 0 ? void 0 : config.persistent,
            hasNoneSort: config === null || config === void 0 ? void 0 : config.hasNoneSort,
            isRowDisabled: isRowDisabled
        });
    });
    interactor.presenter = {
        showOutdatedNotification: () => {
            gridApi === null || gridApi === void 0 ? void 0 : gridApi.showNotification({
                icon: react_1.default.createElement(icons_1.LargeInfoStatusIcon, null),
                content: (texts === null || texts === void 0 ? void 0 : texts.outdatedNotificationMessage) || 'Grid data is outdated',
                canClose: !(config === null || config === void 0 ? void 0 : config.hideIgnoreActionOfOutdatedNotification),
                onDidClose: () => {
                    interactor.didSelectIgnoreFreshData();
                },
                actions: [
                    {
                        icon: react_1.default.createElement(RefreshSvg_1.RefreshSvg, null),
                        text: (texts === null || texts === void 0 ? void 0 : texts.outdatedNotificationRefreshAction) || 'Refresh',
                        handler: () => {
                            interactor.didSelectApplyFreshData();
                        }
                    },
                    {
                        icon: react_1.default.createElement(IgnoreSvg_1.IgnoreSvg, null),
                        text: (texts === null || texts === void 0 ? void 0 : texts.outdatedNotificationIgnoreAction) || 'Ignore',
                        handler: () => interactor.didSelectIgnoreFreshData(),
                        hidden: config === null || config === void 0 ? void 0 : config.hideIgnoreActionOfOutdatedNotification
                    }
                ]
            });
        },
        dismissNotification: () => {
            gridApi.dismissNotification();
        }
    };
    const disabledSelectionState = (0, helpers_1.useStateValue)(disabledSelection, [disabledSelection]);
    const { value, meta } = (0, core_1.useModel)(model);
    const data = value.items;
    if (apiController) {
        apiController.injectApi(gridApi);
    }
    (0, react_1.useEffect)(() => {
        return () => {
            apiController === null || apiController === void 0 ? void 0 : apiController.destroy();
        };
    }, []);
    const persistConfig = interactor.getPersistConfig();
    const getPagination = () => {
        if ((config === null || config === void 0 ? void 0 : config.forcePaginationShownState) === false) {
            return;
        }
        if ((interactor.hasPagination() || (config === null || config === void 0 ? void 0 : config.forcePaginationShownState)) && value.pagination) {
            const activePage = value.pagination.offset / value.pagination.limit + 1;
            return {
                activePage,
                fullPages: Math.ceil((value.totalCount || data.length) / value.pagination.limit),
                onPageChange: interactor.didPageChange.bind(interactor),
                onPageClick: interactor.didPageChange.bind(interactor)
            };
        }
        return;
    };
    const getSorting = () => {
        if (!value.sorting.length) {
            return;
        }
        const sorting = value.sorting[0];
        const matchSortKey = columns.some((i) => i.sortKey === sorting.key);
        if (!matchSortKey) {
            return;
        }
        return {
            key: sorting.key,
            direction: sorting.direction === core_1.SORT_DIRECTION.asc
                ? Grid_1.GRID_SORT_DIRECTIONS.asc
                : Grid_1.GRID_SORT_DIRECTIONS.desc
        };
    };
    const isSelectedAll = interactor.isSelectAll();
    const internalLoading = loading || meta.loading;
    const gridColumnsConfig = columns.map(({ title, id, sortKey, sortCollation, cell, headerCell, canToggleable = true, hidden, highlighted, minWidth, width, justify, onHeaderClick, headerCellContent }) => {
        var _a, _b;
        const columnId = id || sortKey || title || '';
        return {
            field: sortKey || id || '',
            columnId,
            title: title,
            cell: (payload) => {
                const cellElement = cell(payload.rowData, payload);
                if (typeof cellElement === 'string' || typeof cellElement === 'number') {
                    return (0, Grid_1.textCellRenderer)(Object.assign(Object.assign({}, payload), { cellData: String(cellElement) }));
                }
                return cellElement;
            },
            headerCellContent: headerCellContent,
            headerCell: headerCell
                ? (payload) => {
                    return headerCell({
                        id: columnId,
                        title,
                        sortKey,
                        justify,
                        width: payload.columnWidth,
                        minWidth: payload.columnMinWidth,
                        highlighted: !!highlighted,
                        onClick: payload.onHeaderClick
                    });
                }
                : undefined,
            notToggleable: !canToggleable,
            totalColumn: !!highlighted,
            hidden: !((_a = persistConfig.columnVisibility[columnId]) !== null && _a !== void 0 ? _a : !hidden),
            minWidth: minWidth,
            width: (_b = persistConfig.columnSizes[columnId]) !== null && _b !== void 0 ? _b : width,
            justify: justify,
            headerJustify: justify,
            onHeaderClick: onHeaderClick !== null && onHeaderClick !== void 0 ? onHeaderClick : (sortKey
                ? () => {
                    interactor.toggleSort(sortKey, sortCollation);
                }
                : undefined)
        };
    });
    gridColumnsConfig.reduce((acc, item) => {
        if (acc[item.columnId || '']) {
            throw new Error('DataGrid Error: all columns must have uniq id key, define "id" property for each columns');
        }
        acc[item.columnId || ''] = true;
        return acc;
    }, {});
    const selectedItems = [];
    for (let i = 0; i < value.selection.length; i++) {
        const item = model.getItemById(value.selection[i]);
        if (item) {
            selectedItems.push(item);
        }
    }
    const { element: toolbarElement, contextMenuActions } = (0, collectionToolbar_1.buildCollectionToolbar)({
        config: toolbar,
        model: model,
        buildIn: config === null || config === void 0 ? void 0 : config.buildIn,
        payloadGetter: () => ({
            selectedItems,
            loading: internalLoading,
            flags: flags
        })
    });
    const contextMenu = (0, collectionContextMenu_1.useContextMenu)({
        items: contextMenuActions.map((item) => {
            return {
                id: item.id,
                title: item.title || '',
                icon: item.icon,
                decorators: item.decorators,
                disabled: item.disabled,
                hidden: item.hidden,
                handler: () => { var _a; return (_a = item.handler) === null || _a === void 0 ? void 0 : _a.call(item); }
            };
        }),
        payloadGetter: () => ({ selectedItems: selectedItems, loading: internalLoading, flags })
    });
    const getSelectionConfig = () => {
        var _a;
        const findIdKey = () => {
            let idKey = '__internalID__';
            if (selectedItems.length) {
                const item = selectedItems[0];
                const id = model.getItemID(item);
                Object.keys(item).some((key) => {
                    const result = item[key] === id;
                    if (result) {
                        idKey = key;
                    }
                    return result;
                });
            }
            return idKey;
        };
        const defaultConfig = {
            multiple: false,
            field: findIdKey(),
            checkbox: false,
            selected: selectedItems.map((item) => (Object.assign(Object.assign({}, item), { __internalID__: model.getItemID(item) }))),
            fullLength: value.totalCount || 0,
            disableSelectAll: false,
            isSelectedAll: isSelectedAll,
            isIndeterminate: !isSelectedAll && !!((_a = value.selection) === null || _a === void 0 ? void 0 : _a.length)
        };
        if (typeof (config === null || config === void 0 ? void 0 : config.selection) === 'undefined') {
            return defaultConfig;
        }
        if (!(config === null || config === void 0 ? void 0 : config.selection)) {
            return undefined;
        }
        if (typeof config.selection === 'boolean') {
            return defaultConfig;
        }
        return Object.assign(Object.assign({}, defaultConfig), { multiple: !!config.selection.multi, subheaderRenderer: config.selection.subheaderRenderer, checkbox: typeof config.selection.withCheckbox === 'undefined' && config.selection.multi
                ? true
                : !!config.selection.withCheckbox });
    };
    return (react_1.default.createElement(layouts_1.GridBodyLayout, { toolbar: toolbarElement, fragment: react_1.default.createElement(react_1.default.Fragment, null,
            Array.isArray(toolbar === null || toolbar === void 0 ? void 0 : toolbar.fragment)
                ? toolbar === null || toolbar === void 0 ? void 0 : toolbar.fragment.map((i, idx) => (react_1.default.createElement(react_1.default.Fragment, { key: idx }, i)))
                : toolbar === null || toolbar === void 0 ? void 0 : toolbar.fragment,
            contextMenu.element,
            fragment), grid: react_1.default.createElement(Grid_1.Grid, { toggleable: !!(config === null || config === void 0 ? void 0 : config.columnsToggleable), resizable: !!(config === null || config === void 0 ? void 0 : config.canResizeColumns), height: Grid_1.GRID_HEIGHT_MODE.auto, forcePagination: config === null || config === void 0 ? void 0 : config.forcePaginationShownState, data: data.map((item) => Object.assign({}, item, { ['__internalID__']: model.getItemID(item) })), hideHeader: config === null || config === void 0 ? void 0 : config.headless, emptyRenderer: config === null || config === void 0 ? void 0 : config.emptyRenderer, notification: {
                isShown: !!notificationContent,
                onRequestClose: canCloseNotification ? dismissNotification : undefined,
                position: (_b = (_a = config === null || config === void 0 ? void 0 : config.notification) === null || _a === void 0 ? void 0 : _a.position) !== null && _b !== void 0 ? _b : Grid_1.GRID_NOTIFICATION_POSITION.overlay,
                renderContent() {
                    if (notificationContent || typeof notificationContent === 'string') {
                        return react_1.default.createElement(Text_1.Text, null, notificationContent || '');
                    }
                    return notificationContent;
                }
            }, isRowDisabled: isRowDisabled
                ? ({ rowIndex, rowData }) => {
                    return isRowDisabled({
                        rowIndex,
                        rowData,
                        id: model.getItemID(rowData)
                    });
                }
                : undefined, isRowInactive: isRowInactive
                ? ({ rowIndex, rowData }) => {
                    return isRowInactive({
                        rowIndex,
                        rowData,
                        id: model.getItemID(rowData)
                    });
                }
                : undefined, subheader: config === null || config === void 0 ? void 0 : config.subheader, texts: texts, rowHeight: config === null || config === void 0 ? void 0 : config.rowHeight, onColumnResize: (payload) => {
                interactor.didColumnSizeChange({
                    columnId: String(payload.columnId),
                    width: payload.width
                });
            }, isLoading: internalLoading, pagination: getPagination(), disabled: disabled, selection: getSelectionConfig(), onSelectionChange: (item) => {
                if (disabledSelectionState.getValue()) {
                    return;
                }
                interactor.didSelectionChange(item.selected.map((rowData) => model.getItemID(rowData)));
            }, onRowDoubleClick: ({ rowIndex, dataItem }) => {
                onRowDoubleClick === null || onRowDoubleClick === void 0 ? void 0 : onRowDoubleClick({
                    rowIndex,
                    rowData: dataItem,
                    id: model.getItemID(dataItem)
                });
            }, onRowContextMenu: ({ nativeEvent }) => {
                if (!disabled && !internalLoading && !(config === null || config === void 0 ? void 0 : config.disableContextMenu)) {
                    nativeEvent.preventDefault();
                    contextMenu.show({
                        offsetX: nativeEvent.pageX,
                        offsetY: nativeEvent.pageY
                    });
                }
            }, onRowClick: ({ rowIndex, dataItem }) => {
                onRowClick === null || onRowClick === void 0 ? void 0 : onRowClick({
                    rowIndex,
                    rowData: dataItem,
                    id: model.getItemID(dataItem)
                });
            }, onHeaderSelectionChange: () => {
                if (disabledSelectionState.getValue()) {
                    return;
                }
                interactor.didSelectAllChange();
            }, sort: getSorting(), columns: gridColumnsConfig, onColumnToggle: (data) => {
                interactor.didColumnVisibilityChange({
                    columnId: String(data.columnId),
                    isShown: data.isShow
                });
            } }) }));
}
exports.DataGrid = DataGrid;
//# sourceMappingURL=DataGrid.js.map